import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useGames } from "../context/GamesContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const INITIAL = {
  ign: "",
  discord_id: "",
  age: "",
  region: "",
  primary_game: "",
  current_rank: "",
  competitive_history: "",
};

const REGIONS = ["North America", "South America", "EMEA", "APAC", "Oceania"];

export default function Recruitment() {
  const { activeGames } = useGames();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, age: parseInt(form.age, 10) };
      await api.post("/applications", payload);
      toast.success("Application received. Expect contact via Discord.");
      setSuccess(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "bg-[#121212] border-axe-border rounded-xl focus-visible:ring-1 focus-visible:ring-axe-purple focus-visible:border-axe-purple text-white h-12 px-4";

  return (
    <div data-testid="recruitment-page" className="text-white">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 grid lg:grid-cols-2 gap-14 items-start">
        <div className="lg:sticky lg:top-24">
          <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
            Tryouts · Open
          </div>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Sharpen <span className="text-axe-purple">Your</span> Edge
          </h1>
          <p className="mt-6 text-neutral-400 text-base sm:text-lg max-w-lg leading-relaxed">
            Every spot on the AXE roster is earned. Fill the form honestly. We review every
            submission inside 7 days and reach out on Discord for a scrim trial.
          </p>

          <ul className="mt-8 space-y-4 text-sm text-neutral-300">
            {[
              "Minimum rank: Immortal / Master / Crimson equivalent",
              "16+ years old — parental consent for under 18",
              "Committed to 3+ scrim nights per week",
              "English voice communication required",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-axe-tangerine flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <form
          onSubmit={submit}
          className="bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 space-y-5"
          data-testid="recruitment-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                In-Game Name
              </Label>
              <Input
                required
                value={form.ign}
                onChange={(e) => update("ign", e.target.value)}
                className={`mt-2 ${inputCls}`}
                placeholder="YourIGN"
                data-testid="input-ign"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                Discord ID
              </Label>
              <Input
                required
                value={form.discord_id}
                onChange={(e) => update("discord_id", e.target.value)}
                className={`mt-2 ${inputCls}`}
                placeholder="user#0001"
                data-testid="input-discord"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Age</Label>
              <Input
                required
                type="number"
                min="13"
                max="60"
                value={form.age}
                onChange={(e) => update("age", e.target.value)}
                className={`mt-2 ${inputCls}`}
                placeholder="21"
                data-testid="input-age"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Region</Label>
              <Select
                value={form.region}
                onValueChange={(v) => update("region", v)}
              >
                <SelectTrigger
                  className={`mt-2 ${inputCls}`}
                  data-testid="input-region"
                >
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-axe-surface border-axe-border text-white">
                  {REGIONS.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`region-option-${r}`}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                Primary Game
              </Label>
              <Select
                value={form.primary_game}
                onValueChange={(v) => update("primary_game", v)}
              >
                <SelectTrigger
                  className={`mt-2 ${inputCls}`}
                  data-testid="input-primary-game"
                >
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent className="bg-axe-surface border-axe-border text-white">
                  {activeGames.map((g) => (
                    <SelectItem
                      key={g.key}
                      value={g.key}
                      data-testid={`game-option-${g.key}`}
                    >
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">
                Current Rank
              </Label>
              <Input
                required
                value={form.current_rank}
                onChange={(e) => update("current_rank", e.target.value)}
                className={`mt-2 ${inputCls}`}
                placeholder="Immortal 2"
                data-testid="input-rank"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">
              Competitive History
            </Label>
            <Textarea
              required
              value={form.competitive_history}
              onChange={(e) => update("competitive_history", e.target.value)}
              className="mt-2 bg-[#121212] border-axe-border rounded-xl focus-visible:ring-1 focus-visible:ring-axe-purple focus-visible:border-axe-purple text-white p-4 min-h-[140px]"
              placeholder="List previous teams, tournaments placed, notable achievements…"
              data-testid="input-history"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !form.primary_game || !form.region}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-axe-purple px-8 py-3.5 font-bold uppercase tracking-[0.22em] text-white hover:bg-axe-purple-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]"
            data-testid="submit-application"
          >
            {submitting ? "Submitting…" : "Submit Application"}
          </button>

          {success && (
            <div
              className="mt-4 rounded-xl border border-axe-tangerine/50 bg-axe-tangerine/10 p-4 text-sm text-axe-tangerine"
              data-testid="submit-success"
            >
              Submitted. Watch your Discord DMs — scouts will reach out within 7 days.
            </div>
          )}
        </form>
      </section>
    </div>
  );
}
